import { useEffect, useState } from "react";

import {
  cancelMaintenance,
  createAlertRule,
  createFleetView,
  createMaintenance,
  createReportJob,
  deleteAlertRule,
  deleteFleetView,
  fetchOperations,
  fetchProjects,
  mergeIncident,
  type OperationsState,
  previewAlertRule,
  saveServiceVersionPolicy,
  saveVersionPolicy,
  splitIncident,
  updateAlertRule,
  updateFleetView,
  updateIncident,
  updateMaintenance,
} from "./api";
import { encodeFleetView } from "./fleet-view-url";
import { NotificationPanel } from "./NotificationPanel";
import { localDateTimeValue, zonedDateTimeToIso } from "./time";
import type {
  AlertMetric,
  AlertRule,
  FleetColumn,
  FleetNode,
  FleetScope,
  FleetView,
  MaintenanceScope,
  NodeStatus,
  Project,
} from "./domain";
import { createAlertCondition } from "./domain";
import {
  classifyBackupPosture,
  classifyCompliance,
  classifyServiceCompliance,
} from "./operations";

type OperationsData = OperationsState & {
  readonly projects: readonly Project[];
};
type LoadState =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly data: OperationsData };

function scopeFromValue(value: string): FleetScope {
  if (value.startsWith("node:")) {
    return { type: "node", nodeId: value.slice(5) };
  }
  if (value.startsWith("project:")) {
    return { type: "project", projectId: value.slice(8) };
  }
  return { type: "fleet" };
}

function alertRuleFromForm(values: FormData): Omit<AlertRule, "id"> {
  const metric = String(values.get("metric"));
  const comparison = String(values.get("comparison"));
  const severity = String(values.get("severity"));
  if (
    !alertMetrics.includes(metric as AlertMetric) ||
    !alertComparisons.includes(comparison as AlertRule["comparison"]) ||
    !alertSeverities.includes(severity as AlertRule["severity"])
  ) throw new Error("invalid-alert-rule");
  return {
    ...createAlertCondition(
      metric as AlertMetric,
      comparison as AlertRule["comparison"],
      Number(values.get("threshold")),
      Number(values.get("hysteresis")),
    ),
    name: String(values.get("name")),
    durationMinutes: Number(values.get("duration")),
    cooldownMinutes: Number(values.get("cooldown")),
    severity: severity as AlertRule["severity"],
    scope: scopeFromValue(String(values.get("scope"))),
    notificationRoute: values.get("notificationRoute") === "none"
      ? { type: "none" }
      : { type: "ntfy", secretRef: "NTFY_URL" },
    enabled: values.get("enabled") === "on",
  };
}

function commaValues(value: FormDataEntryValue | null): string[] {
  return String(value ?? "").split(",").map((item) => item.trim()).filter(
    Boolean,
  );
}

const operationalStatuses = [
  "healthy",
  "warning",
  "critical",
  "stale",
  "offline",
] as const satisfies readonly NodeStatus[];
const fleetSorts = [
  "name",
  "status",
  "last-seen",
] as const satisfies readonly FleetView["sort"][];
const fleetColumns = [
  "status",
  "name",
  "project",
  "location",
  "release",
  "disk",
  "memory",
  "backup",
  "update",
  "last-seen",
] as const satisfies readonly FleetColumn[];
const fleetGroups = [
  "none",
  "project",
  "status",
  "location",
] as const satisfies readonly FleetView["groupBy"][];
const alertMetrics = [
  "contact-age",
  "cpu",
  "memory",
  "disk",
  "container-health",
  "restart-count",
  "backup-age",
  "version-compliance",
] as const satisfies readonly AlertMetric[];
const alertComparisons = [
  "gt",
  "gte",
  "lt",
  "lte",
  "eq",
] as const satisfies readonly AlertRule["comparison"][];
const alertSeverities = [
  "warning",
  "critical",
] as const satisfies readonly AlertRule["severity"][];
const softwareComponents = [
  "agent",
  "appliance",
  "os",
  "docker",
  "compose",
] as const;

const operationTabs = [
  { id: "planning", label: "Planning" },
  { id: "incidents", label: "Incidents" },
  { id: "compliance", label: "Compliance" },
  { id: "reports", label: "Reports & recovery" },
  { id: "configuration", label: "Configuration" },
] as const;

type OperationTab = typeof operationTabs[number]["id"];

function isFleetSort(value: string): value is FleetView["sort"] {
  return fleetSorts.some((candidate) => candidate === value);
}

function fleetViewFromForm(values: FormData): Omit<FleetView, "id"> {
  const statuses = commaValues(values.get("statuses")).filter((
    status,
  ): status is NodeStatus =>
    operationalStatuses.includes(status as NodeStatus)
  );
  const maxAge = String(values.get("maxAge") ?? "").trim();
  const sortValue = String(values.get("sort") ?? "name");
  const columns = commaValues(values.get("columns")).filter((
    column,
  ): column is FleetColumn => fleetColumns.includes(column as FleetColumn));
  const groupBy = String(values.get("groupBy") ?? "none");
  return {
    name: String(values.get("name")),
    filter: {
      query: String(values.get("query") ?? ""),
      statuses,
      tags: commaValues(values.get("tags")),
      projectIds: commaValues(values.get("projects")),
      locations: commaValues(values.get("locations")),
      releases: commaValues(values.get("releases")),
      capabilities: commaValues(values.get("capabilities")),
      maxContactAgeMinutes: maxAge ? Number(maxAge) : null,
    },
    sort: isFleetSort(sortValue) ? sortValue : "name",
    columns: columns.length ? columns : [...fleetColumns],
    groupBy: fleetGroups.includes(groupBy as FleetView["groupBy"])
      ? groupBy as FleetView["groupBy"]
      : "none",
    isDefault: values.get("default") === "on",
  };
}

function maintenanceScopeFromForm(values: FormData): MaintenanceScope {
  const nodeIds = values.getAll("selectedNodeIds").map(String);
  return nodeIds.length > 1
    ? { type: "nodes", nodeIds }
    : scopeFromValue(String(values.get("scope")));
}

function scopeValue(scope: FleetScope): string {
  return scope.type === "node"
    ? `node:${scope.nodeId}`
    : scope.type === "project"
    ? `project:${scope.projectId}`
    : "fleet";
}

function reportScopeLabel(
  scope: OperationsState["reportJobs"][number]["scope"],
  projects: readonly Project[],
): string {
  if (scope.type === "fleet") return "fleet";
  return `project ${
    projects.find((project) => project.id === scope.projectId)?.name ??
      scope.projectId
  }`;
}

/** Render every bounded field needed to create or edit an alert rule. */
function AlertRuleFields(
  { rule, nodes, projects }: {
    readonly rule?: AlertRule;
    readonly nodes: readonly FleetNode[];
    readonly projects: readonly Project[];
  },
) {
  return (
    <>
      <label>
        Rule name<input
          name="name"
          required
          maxLength={80}
          defaultValue={rule?.name}
        />
      </label>
      <label>
        Metric<select name="metric" defaultValue={rule?.metric ?? "disk"}>
          {alertMetrics.map((metric) => (
            <option key={metric} value={metric}>{metric}</option>
          ))}
        </select>
      </label>
      <label>
        Comparison<select
          name="comparison"
          defaultValue={rule?.comparison ?? "gte"}
        >
          <option value="gt">Greater than</option>
          <option value="gte">Greater than or equal</option>
          <option value="lt">Less than</option>
          <option value="lte">Less than or equal</option>
          <option value="eq">Equal</option>
        </select>
      </label>
      <label>
        Threshold<input
          name="threshold"
          type="number"
          step="any"
          defaultValue={rule?.threshold ?? 85}
          required
        />
      </label>
      <label>
        Duration in minutes<input
          name="duration"
          type="number"
          min={0}
          max={10_080}
          defaultValue={rule?.durationMinutes ?? 10}
          required
        />
      </label>
      <label>
        Recovery hysteresis<input
          name="hysteresis"
          type="number"
          min={0}
          step="any"
          defaultValue={rule?.hysteresis ?? 5}
          required
        />
      </label>
      <label>
        Cooldown in minutes<input
          name="cooldown"
          type="number"
          min={1}
          max={10_080}
          defaultValue={rule?.cooldownMinutes ?? 30}
          required
        />
      </label>
      <label>
        Severity<select
          name="severity"
          defaultValue={rule?.severity ?? "critical"}
        >
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </select>
      </label>
      <label>
        Scope<select
          name="scope"
          defaultValue={rule ? scopeValue(rule.scope) : "fleet"}
        >
          <option value="fleet">Entire fleet</option>
          {projects.map((project) => (
            <option key={project.id} value={`project:${project.id}`}>
              Project: {project.name}
            </option>
          ))}
          {nodes.map((node) => (
            <option key={node.id} value={`node:${node.id}`}>
              Node: {node.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Notification route<select
          name="notificationRoute"
          defaultValue={rule?.notificationRoute.type ?? "ntfy"}
        >
          <option value="ntfy">Private ntfy topic (Worker secrets)</option>
          <option value="none">No outbound notification</option>
        </select>
      </label>
      <label>
        <input
          name="enabled"
          type="checkbox"
          defaultChecked={rule?.enabled ?? true}
        />{" "}
        Enabled
      </label>
    </>
  );
}

/** Render dedicated administration for views, maintenance, alerts, incidents, compliance, reports, and backups. */
export function Operations(
  { nodes, csrf, now }: {
    readonly nodes: readonly FleetNode[];
    readonly csrf: string;
    readonly now: Date;
  },
) {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [message, setMessage] = useState("");
  const [alertPreview, setAlertPreview] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<OperationTab>("planning");
  const load = async (): Promise<void> => {
    try {
      const [operations, projects] = await Promise.all([
        fetchOperations(),
        fetchProjects(),
      ]);
      setState({ status: "ready", data: { ...operations, projects } });
    } catch {
      setState({ status: "error" });
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const run = async (
    operation: () => Promise<void>,
    success: string,
  ): Promise<void> => {
    setMessage("");
    try {
      await operation();
      setMessage(success);
      await load();
    } catch {
      setMessage("The change was not saved. Review the fields and try again.");
    }
  };
  if (state.status === "loading") {
    return (
      <section>
        <h2>Operations</h2>
        <p role="status">Loading operational controls…</p>
      </section>
    );
  }
  if (state.status === "error") {
    return (
      <section>
        <h2>Operations unavailable</h2>
        <button type="button" onClick={() => void load()}>Try again</button>
      </section>
    );
  }
  const data = state.data;
  const desiredAppliance = data.policies.find((policy) =>
    policy.component === "appliance"
  );
  return (
    <section className="operations" aria-labelledby="operations-title">
      <div className="section-heading">
        <div>
          <h2 id="operations-title">Fleet operations</h2>
          <p>
            Observation policy, planned work, incidents, compliance, and
            evidence exports
          </p>
        </div>
      </div>
      {message ? <p role="status">{message}</p> : null}
      <div className="operation-tabs" role="tablist" aria-label="Operations sections">
        {operationTabs.map((tab, index) => (
          <button
            id={`operations-tab-${tab.id}`}
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls="operations-panel"
            tabIndex={activeTab === tab.id ? 0 : -1}
            onClick={() => setActiveTab(tab.id)}
            onKeyDown={(event) => {
              if (
                event.key !== "ArrowLeft" && event.key !== "ArrowRight" &&
                event.key !== "Home" && event.key !== "End"
              ) return;
              event.preventDefault();
              const next = event.key === "Home"
                ? 0
                : event.key === "End"
                ? operationTabs.length - 1
                : (index + (event.key === "ArrowRight" ? 1 : -1) +
                  operationTabs.length) % operationTabs.length;
              const nextTab = operationTabs[next];
              if (!nextTab) return;
              setActiveTab(nextTab.id);
              document.getElementById(`operations-tab-${nextTab.id}`)?.focus();
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div
        className="operations-grid"
        id="operations-panel"
        role="tabpanel"
        aria-labelledby={`operations-tab-${activeTab}`}
        tabIndex={0}
      >
        <section className="project-card" hidden={activeTab !== "planning"} aria-labelledby="operations-tab-planning">
          <h3>Saved fleet views</h3>
          <p>{data.views.length} saved views</p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(() =>
                createFleetView(
                  fleetViewFromForm(new FormData(event.currentTarget)),
                  csrf,
                ), "View saved.");
            }}
          >
            <label>
              View name<input name="name" required maxLength={80} />
            </label>
            <label>
              Search text<input name="query" maxLength={120} />
            </label>
            <label>
              Statuses<input name="statuses" placeholder="warning, critical" />
            </label>
            <label>
              Tags<input name="tags" maxLength={200} />
            </label>
            <label>
              Project IDs<input name="projects" />
            </label>
            <label>
              Locations<input name="locations" />
            </label>
            <label>
              Releases<input name="releases" />
            </label>
            <label>
              Capabilities<input name="capabilities" />
            </label>
            <label>
              Maximum contact age in minutes<input
                name="maxAge"
                type="number"
                min={0}
                max={525600}
              />
            </label>
            <label>
              Sort<select name="sort" defaultValue="name">
                <option value="name">Name</option>
                <option value="status">Status</option>
                <option value="last-seen">Last seen</option>
              </select>
            </label>
            <label>
              Visible columns<input
                name="columns"
                defaultValue={fleetColumns.join(", ")}
                aria-describedby="fleet-columns-help"
              />
            </label>
            <small id="fleet-columns-help">
              Comma-separated: {fleetColumns.join(", ")}.
            </small>
            <label>
              Group nodes by<select name="groupBy" defaultValue="none">
                {fleetGroups.map((group) => (
                  <option key={group} value={group}>{group}</option>
                ))}
              </select>
            </label>
            <label>
              <input name="default" type="checkbox" /> Default view
            </label>
            <button type="submit">Save view</button>
          </form>
          <ul>
            {data.views.map((view) => (
              <li key={view.id}>
                <a href={encodeFleetView(view)}>
                  {view.name}
                </a>
                {view.isDefault ? " — default" : ""}
                <details>
                  <summary>Edit view</summary>
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      void run(() =>
                        updateFleetView(
                          view.id,
                          fleetViewFromForm(new FormData(event.currentTarget)),
                          csrf,
                        ), "View updated.");
                    }}
                  >
                    <label>
                      Name<input
                        name="name"
                        defaultValue={view.name}
                        required
                        maxLength={80}
                      />
                    </label>
                    <label>
                      Search text<input
                        name="query"
                        defaultValue={view.filter.query}
                      />
                    </label>
                    <label>
                      Statuses<input
                        name="statuses"
                        defaultValue={view.filter.statuses.join(", ")}
                      />
                    </label>
                    <label>
                      Tags<input
                        name="tags"
                        defaultValue={view.filter.tags.join(", ")}
                      />
                    </label>
                    <label>
                      Project IDs<input
                        name="projects"
                        defaultValue={(view.filter.projectIds ?? []).join(", ")}
                      />
                    </label>
                    <label>
                      Locations<input
                        name="locations"
                        defaultValue={(view.filter.locations ?? []).join(", ")}
                      />
                    </label>
                    <label>
                      Releases<input
                        name="releases"
                        defaultValue={(view.filter.releases ?? []).join(", ")}
                      />
                    </label>
                    <label>
                      Capabilities<input
                        name="capabilities"
                        defaultValue={(view.filter.capabilities ?? []).join(
                          ", ",
                        )}
                      />
                    </label>
                    <label>
                      Maximum contact age in minutes<input
                        name="maxAge"
                        type="number"
                        min={0}
                        max={525600}
                        defaultValue={view.filter.maxContactAgeMinutes ?? ""}
                      />
                    </label>
                    <label>
                      Sort<select name="sort" defaultValue={view.sort}>
                        <option value="name">Name</option>
                        <option value="status">Status</option>
                        <option value="last-seen">Last seen</option>
                      </select>
                    </label>
                    <label>
                      Visible columns<input
                        name="columns"
                        defaultValue={view.columns.join(", ")}
                      />
                    </label>
                    <label>
                      Group nodes by<select
                        name="groupBy"
                        defaultValue={view.groupBy}
                      >
                        {fleetGroups.map((group) => (
                          <option key={group} value={group}>{group}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <input
                        name="default"
                        type="checkbox"
                        defaultChecked={view.isDefault}
                      />{" "}
                      Default view
                    </label>
                    <button type="submit">Save changes</button>
                  </form>
                </details>
                <button
                  type="button"
                  onClick={() =>
                    void run(
                      () => deleteFleetView(view.id, csrf),
                      "View deleted.",
                    )}
                >
                  Delete view
                </button>
              </li>
            ))}
          </ul>
        </section>
        <section className="project-card" hidden={activeTab !== "planning"} aria-labelledby="operations-tab-planning">
          <h3>Maintenance windows</h3>
          <p>
            {data.maintenance.filter((window) => window.state === "scheduled")
              .length} scheduled
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const values = new FormData(event.currentTarget);
              const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
              const disambiguation = values.get("ambiguity") === "later" ? "later" : "earlier";
              void run(() =>
                createMaintenance({
                  scope: maintenanceScopeFromForm(values),
                  startsAt: zonedDateTimeToIso(String(values.get("starts")), timezone, disambiguation),
                  endsAt: zonedDateTimeToIso(String(values.get("ends")), timezone, disambiguation),
                  timezone,
                  reason: String(values.get("reason")),
                }, csrf), "Maintenance scheduled.");
            }}
          >
            <label>
              Scope<select name="scope" defaultValue="fleet">
                <option value="fleet">Entire fleet</option>
                {data.projects.map((project) => (
                  <option key={project.id} value={`project:${project.id}`}>
                    Project: {project.name}
                  </option>
                ))}
                {nodes.map((node) => (
                  <option key={node.id} value={`node:${node.id}`}>
                    Node: {node.name}
                  </option>
                ))}
              </select>
            </label>
            <fieldset>
              <legend>Or select two or more nodes</legend>
              {nodes.map((node) => (
                <label key={node.id}>
                  <input
                    type="checkbox"
                    name="selectedNodeIds"
                    value={node.id}
                  />
                  {node.name}
                </label>
              ))}
            </fieldset>
            <label>
              Starts<input name="starts" type="datetime-local" required />
            </label>
            <label>
              Ends<input name="ends" type="datetime-local" required />
            </label>
            <label>
              Repeated-hour choice<select name="ambiguity" defaultValue="earlier">
                <option value="earlier">Earlier occurrence</option>
                <option value="later">Later occurrence</option>
              </select>
            </label>
            <label>
              Reason<input name="reason" required maxLength={300} />
            </label>
            <button type="submit">Schedule maintenance</button>
          </form>
          <ul>
            {data.maintenance.map((window) => (
              <li key={window.id}>
                {window.reason} — {window.scope.type} — {window.state} —{" "}
                <time dateTime={window.startsAt}>
                  {new Date(window.startsAt).toLocaleString()}
                </time>
                {window.state === "scheduled"
                  ? (
                    <>
                      <details>
                        <summary>Edit window</summary>
                        <form
                          onSubmit={(event) => {
                            event.preventDefault();
                            const values = new FormData(event.currentTarget);
                            const timezone = window.timezone;
                            const disambiguation = values.get("ambiguity") === "later" ? "later" : "earlier";
                            void run(() =>
                              updateMaintenance(window.id, {
                                scope: window.scope,
                                startsAt: zonedDateTimeToIso(String(values.get("starts")), timezone, disambiguation, window.startsAt),
                                endsAt: zonedDateTimeToIso(String(values.get("ends")), timezone, disambiguation, window.endsAt),
                                timezone,
                                reason: String(values.get("reason")),
                              }, csrf), "Maintenance updated.");
                          }}
                        >
                          <label>
                            Starts<input
                              name="starts"
                              type="datetime-local"
                              defaultValue={localDateTimeValue(window.startsAt, window.timezone)}
                              required
                            />
                          </label>
                          <label>
                            Ends<input
                              name="ends"
                              type="datetime-local"
                              defaultValue={localDateTimeValue(window.endsAt, window.timezone)}
                              required
                            />
                          </label>
                          <label>
                            Repeated-hour choice<select name="ambiguity" defaultValue="earlier">
                              <option value="earlier">Earlier occurrence</option>
                              <option value="later">Later occurrence</option>
                            </select>
                          </label>
                          <label>
                            Reason<input
                              name="reason"
                              defaultValue={window.reason}
                              required
                              maxLength={300}
                            />
                          </label>
                          <button type="submit">Save changes</button>
                        </form>
                      </details>
                      <button
                        type="button"
                        onClick={() =>
                          void run(
                            () => cancelMaintenance(window.id, csrf),
                            "Maintenance cancelled.",
                          )}
                      >
                        Cancel window
                      </button>
                    </>
                  )
                  : null}
              </li>
            ))}
          </ul>
        </section>
        <section className="project-card" hidden={activeTab !== "planning"} aria-labelledby="operations-tab-planning">
          <h3>Alert rules</h3>
          <p>{data.rules.filter((rule) => rule.enabled).length} active</p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const form = event.currentTarget;
              const rule = alertRuleFromForm(new FormData(form));
              if (form.dataset.intent === "preview") {
                form.dataset.intent = "create";
                void previewAlertRule(rule, csrf).then((preview) =>
                  setAlertPreview(
                    `${preview.matchedSamples} of ${preview.sampleCount} retained samples across ${preview.matchedNodeCount} nodes would match.`,
                  )
                ).catch(() =>
                  setAlertPreview("Preview could not be calculated.")
                );
              } else {void run(
                  () => createAlertRule(rule, csrf),
                  "Alert rule created.",
                );}
            }}
          >
            <label>
              Rule name<input name="name" required maxLength={80} />
            </label>
            <label>
              Metric<select name="metric" defaultValue="disk">
                <option value="contact-age">Contact age in minutes</option>
                <option value="memory">Memory percent</option>
                <option value="disk">Disk percent</option>
                <option value="container-health">Unhealthy containers</option>
                <option value="restart-count">Container restart count</option>
                <option value="backup-age">Backup age in days</option>
                <option value="version-compliance">
                  Version non-compliance
                </option>
                <option value="cpu">CPU percent when reported</option>
              </select>
            </label>
            <label>
              Comparison<select name="comparison" defaultValue="gte">
                <option value="gt">Greater than</option>
                <option value="gte">Greater than or equal</option>
                <option value="lt">Less than</option>
                <option value="lte">Less than or equal</option>
                <option value="eq">Equal</option>
              </select>
            </label>
            <label>
              Threshold<input
                name="threshold"
                type="number"
                step="any"
                defaultValue={85}
                required
              />
            </label>
            <label>
              Duration in minutes<input
                name="duration"
                type="number"
                min={0}
                max={10_080}
                defaultValue={10}
                required
              />
            </label>
            <label>
              Recovery hysteresis<input
                name="hysteresis"
                type="number"
                min={0}
                step="any"
                defaultValue={5}
                required
              />
            </label>
            <label>
              Cooldown in minutes<input
                name="cooldown"
                type="number"
                min={1}
                max={10_080}
                defaultValue={30}
                required
              />
            </label>
            <label>
              Severity<select name="severity" defaultValue="critical">
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </label>
            <label>
              Scope<select name="scope" defaultValue="fleet">
                <option value="fleet">Entire fleet</option>
                {data.projects.map((project) => (
                  <option key={project.id} value={`project:${project.id}`}>
                    Project: {project.name}
                  </option>
                ))}
                {nodes.map((node) => (
                  <option key={node.id} value={`node:${node.id}`}>
                    Node: {node.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <input name="enabled" type="checkbox" defaultChecked /> Enabled
            </label>
            <div className="form-actions">
              <button
                type="submit"
                onClick={(event) => {
                  if (event.currentTarget.form) {
                    event.currentTarget.form
                      .dataset.intent = "preview";
                  }
                }}
              >
                Preview against 7 days
              </button>
              <button type="submit">Create rule</button>
            </div>
            {alertPreview ? <p role="status">{alertPreview}</p> : null}
          </form>
          <ul>
            {data.rules.map((rule) => (
              <li key={rule.id}>
                {rule.name} — {rule.metric} {rule.comparison} {rule.threshold}
                <details>
                  <summary>Edit rule</summary>
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      const values = new FormData(event.currentTarget);
                      void run(() =>
                        updateAlertRule(rule.id, {
                          ...rule,
                          name: String(values.get("name")),
                          threshold: Number(values.get("threshold")),
                          enabled: values.get("enabled") === "on",
                        }, csrf), "Alert rule updated.");
                    }}
                  >
                    <label>
                      Name<input
                        name="name"
                        defaultValue={rule.name}
                        required
                        maxLength={80}
                      />
                    </label>
                    <label>
                      Threshold<input
                        name="threshold"
                        type="number"
                        step="any"
                        defaultValue={rule.threshold}
                        required
                      />
                    </label>
                    <label>
                      <input
                        name="enabled"
                        type="checkbox"
                        defaultChecked={rule.enabled}
                      />{" "}
                      Enabled
                    </label>
                    <button type="submit">Save changes</button>
                  </form>
                </details>
                <button
                  type="button"
                  onClick={() =>
                    void run(
                      () => deleteAlertRule(rule.id, csrf),
                      "Alert rule deleted.",
                    )}
                >
                  Delete rule
                </button>
              </li>
            ))}
          </ul>
        </section>
        <section className="project-card" hidden={activeTab !== "incidents"} aria-labelledby="operations-tab-incidents">
          <h3>Incident timeline</h3>
          {data.incidents.length === 0 ? <p>No retained incidents.</p> : (
            <ol>
              {data.incidents.map((incident) => {
                const events = data.incidentEvents.filter((event) =>
                  event.incidentId === incident.id
                );
                return (
                  <li key={incident.id}>
                    <strong>{incident.title}</strong> — {incident.state}{" "}
                    <time dateTime={incident.openedAt}>
                      {new Date(incident.openedAt).toLocaleString()}
                    </time>
                    {incident.state === "open"
                      ? (
                        <button
                          type="button"
                          onClick={() =>
                            void run(() =>
                              updateIncident(
                                incident.id,
                                "acknowledge",
                                csrf,
                              ), "Incident acknowledged.")}
                        >
                          Acknowledge
                        </button>
                      )
                      : null}
                    {incident.state !== "resolved"
                      ? (
                        <button
                          type="button"
                          onClick={() =>
                            void run(
                              () =>
                                updateIncident(incident.id, "resolve", csrf),
                              "Incident resolved.",
                            )}
                        >
                          Resolve
                        </button>
                      )
                      : null}
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        const values = new FormData(event.currentTarget);
                        void run(() =>
                          updateIncident(
                            incident.id,
                            "note",
                            csrf,
                            String(values.get("note")),
                          ), "Incident note added.");
                      }}
                    >
                      <label>
                        Add note<input name="note" required maxLength={500} />
                      </label>
                      <button type="submit">Add note</button>
                    </form>
                    {incident.state !== "resolved" &&
                        data.incidents.some((candidate) =>
                          candidate.id !== incident.id &&
                          candidate.state !== "resolved"
                        )
                      ? (
                        <form
                          onSubmit={(event) => {
                            event.preventDefault();
                            const values = new FormData(event.currentTarget);
                            void run(() =>
                              mergeIncident(
                                incident.id,
                                String(values.get("target")),
                                csrf,
                              ), "Incidents merged.");
                          }}
                        >
                          <label>
                            Merge into<select name="target" required>
                              {data.incidents.filter((candidate) =>
                                candidate.id !== incident.id &&
                                candidate.state !== "resolved"
                              ).map((candidate) => (
                                <option
                                  key={candidate.id}
                                  value={candidate.id}
                                >
                                  {candidate.title}
                                </option>
                              ))}
                            </select>
                          </label>
                          <button type="submit">Merge incident</button>
                        </form>
                      )
                      : null}
                    <ol>
                      {events.map((event) => (
                        <li key={event.id}>
                          <time dateTime={event.occurredAt}>
                            {new Date(event.occurredAt).toLocaleString()}
                          </time>{" "}
                          — {event.kind}: {event.message}
                        </li>
                      ))}
                    </ol>
                    {events.length > 1
                      ? (
                        <form
                          onSubmit={(event) => {
                            event.preventDefault();
                            const values = new FormData(event.currentTarget);
                            const eventIds = values.getAll("eventIds").map(
                              String,
                            );
                            void run(() =>
                              splitIncident(
                                incident.id,
                                eventIds,
                                String(values.get("title")),
                                csrf,
                              ), "Incident split.");
                          }}
                        >
                          <label>
                            New incident title<input
                              name="title"
                              required
                              maxLength={160}
                            />
                          </label>
                          <fieldset>
                            <legend>Events to move</legend>
                            {events.map((event) => (
                              <label key={event.id}>
                                <input
                                  type="checkbox"
                                  name="eventIds"
                                  value={event.id}
                                />
                                {event.message}
                              </label>
                            ))}
                          </fieldset>
                          <button type="submit">Split selected events</button>
                        </form>
                      )
                      : null}
                  </li>
                );
              })}
            </ol>
          )}
        </section>
        <section className="project-card" hidden={activeTab !== "compliance"} aria-labelledby="operations-tab-compliance">
          <h3>Version compliance</h3>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const values = new FormData(event.currentTarget);
              void run(() =>
                saveVersionPolicy({
                  component: "appliance",
                  desiredVersion: String(values.get("version")),
                  minimumSupportedVersion: String(values.get("minimum")),
                  immutableRequired: values.get("immutable") === "on",
                }, csrf), "Version policy saved.");
            }}
          >
            <label>
              Desired appliance version<input
                name="version"
                defaultValue={desiredAppliance?.desiredVersion ?? ""}
                required
                maxLength={128}
              />
            </label>
            <label>
              Minimum supported version<input
                name="minimum"
                defaultValue={desiredAppliance?.minimumSupportedVersion ?? ""}
                maxLength={128}
              />
            </label>
            <label>
              <input
                name="immutable"
                type="checkbox"
                defaultChecked={desiredAppliance?.immutableRequired ?? true}
              />{" "}
              Require immutable version evidence
            </label>
            <button type="submit">Save policy</button>
          </form>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Node</th>
                  <th>Target</th>
                  <th>Appliance</th>
                  <th>OS</th>
                  <th>Docker</th>
                  <th>Compose</th>
                  <th>Policy</th>
                </tr>
              </thead>
              <tbody>
                {data.software.length
                  ? data.software.map((software) => (
                    <tr key={software.nodeId}>
                      <td>
                        {software.projectNames.join(", ") || "Unassigned"}
                      </td>
                      <td>{software.nodeName}</td>
                      <td>{software.target}</td>
                      <td>{software.appliance ?? "Unknown"}</td>
                      <td>{software.os ?? "Unknown"}</td>
                      <td>{software.docker ?? "Unknown"}</td>
                      <td>{software.compose ?? "Unknown"}</td>
                      <td>
                        {desiredAppliance
                          ? classifyCompliance(
                            software.appliance ?? undefined,
                            desiredAppliance.desiredVersion,
                            software.immutable &&
                              desiredAppliance.immutableRequired,
                            desiredAppliance.minimumSupportedVersion,
                          )
                          : "unknown"}
                      </td>
                    </tr>
                  ))
                  : nodes.map((node) => (
                    <tr key={node.id}>
                      <td>{node.projectNames?.join(", ") || "Unassigned"}</td>
                      <td>{node.name}</td>
                      <td>Unknown</td>
                      <td>{node.release}</td>
                      <td>Unknown</td>
                      <td>Unknown</td>
                      <td>Unknown</td>
                      <td>
                        {desiredAppliance
                          ? classifyCompliance(
                            node.release,
                            desiredAppliance.desiredVersion,
                            desiredAppliance.immutableRequired,
                            desiredAppliance.minimumSupportedVersion,
                          )
                          : "unknown"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p>Compliance is read-only. Apply updates locally on each node.</p>
        </section>
        <section className="project-card" hidden={activeTab !== "compliance"} aria-labelledby="operations-tab-compliance">
          <h3>Service image compliance</h3>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const values = new FormData(event.currentTarget);
              void run(() =>
                saveServiceVersionPolicy({
                  catalogId: String(values.get("catalogId")),
                  desiredImage: String(values.get("image")),
                  desiredDigest: String(values.get("digest")),
                }, csrf), "Service image policy saved.");
            }}
          >
            <label>
              Catalogue ID<input
                name="catalogId"
                required
                pattern="[a-z0-9][a-z0-9._-]{0,63}"
              />
            </label>
            <label>
              Desired image<input
                name="image"
                required
                maxLength={300}
                placeholder="vendor/service:version"
              />
            </label>
            <label>
              Registry digest<input
                name="digest"
                required
                pattern="sha256:[a-f0-9]{64}"
                placeholder="sha256:…"
              />
            </label>
            <button type="submit">Save immutable policy</button>
          </form>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Node</th>
                  <th>Service</th>
                  <th>Reported image</th>
                  <th>Digest evidence</th>
                  <th>Policy</th>
                  <th>Local remediation</th>
                </tr>
              </thead>
              <tbody>
                {data.serviceInventory.length === 0
                  ? (
                    <tr>
                      <td colSpan={6}>No service inventory reported.</td>
                    </tr>
                  )
                  : data.serviceInventory.map((service) => {
                    const policy = data.servicePolicies.find((candidate) =>
                      candidate.catalogId === service.catalogId
                    );
                    const classification = classifyServiceCompliance(
                      service.imageDigest,
                      policy?.desiredDigest,
                    );
                    return (
                      <tr key={`${service.nodeId}-${service.catalogId}`}>
                        <td>{service.nodeName}</td>
                        <td>{service.catalogId}</td>
                        <td>{service.image}</td>
                        <td>{service.imageDigest ?? "Unavailable"}</td>
                        <td>{classification}</td>
                        <td>
                          {policy && classification !== "compliant"
                            ? (
                              <code>
                                {`docker pull ${policy.desiredImage}@${policy.desiredDigest}`}
                              </code>
                            )
                            : "None"}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <p>
            Commands are copy-only and must be reviewed and run locally on the
            relevant Docker host.
          </p>
        </section>
        <section className="project-card" hidden={activeTab !== "reports"} aria-labelledby="operations-tab-reports">
          <h3>Reports</h3>
          <p>
            Small CSV exports stream immediately. PDFs are generated out of the
            request path, stored privately, and expire after 24 hours.
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              const values = new FormData(event.currentTarget);
              const scopeValue = String(values.get("reportScope"));
              void run(() =>
                createReportJob({
                  window: String(values.get("window")) as "24h" | "7d" | "30d",
                  format: String(values.get("format")) === "csv"
                    ? "csv"
                    : "pdf",
                  scope: scopeValue.startsWith("project:")
                    ? { type: "project", projectId: scopeValue.slice(8) }
                    : { type: "fleet" },
                }, csrf), "Private report queued.");
            }}
          >
            <label>
              Evidence period<select name="window" defaultValue="7d">
                <option value="24h">24 hours</option>
                <option value="7d">7 days</option>
                <option value="30d">30 days</option>
              </select>
            </label>
            <label>
              Scope<select name="reportScope" defaultValue="fleet">
                <option value="fleet">Entire fleet</option>
                {data.projects.map((project) => (
                  <option key={project.id} value={`project:${project.id}`}>
                    Project: {project.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Format<select name="format" defaultValue="pdf">
                <option value="pdf">Branded PDF</option>
                <option value="csv">Queued CSV</option>
              </select>
            </label>
            <button type="submit">Queue private report</button>
          </form>
          <div className="form-actions">
            <a
              className="button-link"
              href="/api/admin/reports/fleet.csv?window=7d"
            >
              Download 7-day CSV
            </a>
            <a className="button-link" href="/api/admin/reports/inventory.csv">
              Inventory CSV
            </a>
          </div>
          <h4>Recent report jobs</h4>
          {data.reportJobs.length === 0
            ? <p>No report jobs requested.</p>
            : (
              <ul>
                {data.reportJobs.map((job) => (
                  <li key={job.id}>
                    <time dateTime={job.requestedAt}>
                      {new Date(job.requestedAt).toLocaleString()}
                    </time>{" "}
                    — {reportScopeLabel(job.scope, data.projects)} —{" "}
                    {job.window} {job.format} — {job.state}
                    {job.state === "ready"
                      ? (
                        <a
                          href={`/api/admin/report-jobs/${
                            encodeURIComponent(job.id)
                          }/download`}
                        >
                          Download before{" "}
                          {new Date(job.expiresAt).toLocaleString()}
                        </a>
                      )
                      : null}
                    {job.errorCode ? ` — ${job.errorCode}` : ""}
                  </li>
                ))}
              </ul>
            )}
        </section>
        <section className="project-card" hidden={activeTab !== "reports"} aria-labelledby="operations-tab-reports">
          <h3>Dashboard backup posture</h3>
          {data.backups.length === 0
            ? <p>No signed backup manifests received.</p>
            : (
              <ul>
                {data.backups.map((backup) => (
                  <li key={backup.id}>
                    {backup.target} — {classifyBackupPosture({
                      capturedAt: backup.capturedAt,
                      restoreTestedAt: backup.restoreTestedAt,
                      restoreSchemaVerified: backup.restoreSchemaVerified,
                      restoreDecryptionVerified:
                        backup.restoreDecryptionVerified,
                      keyEscrowVerified: backup.keyEscrowVerified,
                      state: backup.state,
                    }, now)}
                    {backup.errorCode ? ` — ${backup.errorCode}` : ""}
                  </li>
                ))}
              </ul>
            )}
          <p>
            Freshness, key escrow, schema migration, and encrypted project
            decryption are evaluated separately. Restores remain a documented
            CLI-only operation.
          </p>
        </section>
        <section className="project-card" hidden={activeTab !== "configuration"} aria-labelledby="operations-tab-configuration">
          <h3>Complete alert policy editor</h3>
          <p>
            Edit metric, scope, timing, severity, recovery hysteresis, and
            notification cooldown together.
          </p>
          {data.rules.length === 0
            ? <p>No alert rules to edit.</p>
            : data.rules.map((rule) => (
              <details key={rule.id}>
                <summary>{rule.name}</summary>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void run(() =>
                      updateAlertRule(
                        rule.id,
                        alertRuleFromForm(new FormData(event.currentTarget)),
                        csrf,
                      ), "Alert policy updated.");
                  }}
                >
                  <AlertRuleFields
                    rule={rule}
                    nodes={nodes}
                    projects={data.projects}
                  />
                  <button type="submit">Save complete policy</button>
                </form>
                <button
                  type="button"
                  onClick={() =>
                    void previewAlertRule({ ...rule }, csrf).then((preview) =>
                      setAlertPreview(
                        `${preview.firingEpisodes} firing episodes, ${preview.recoveries} recoveries, and ${preview.suppressedSamples} maintenance-suppressed samples across ${preview.sampleCount} retained observations.`,
                      )
                    ).catch(() =>
                      setAlertPreview("Preview could not be calculated.")
                    )}
                >
                  Preview this rule
                </button>
              </details>
            ))}
          {alertPreview ? <p role="status">{alertPreview}</p> : null}
        </section>
        <section className="project-card" hidden={activeTab !== "compliance"} aria-labelledby="operations-tab-compliance">
          <h3>Software policy matrix</h3>
          <p>
            Set a read-only compliance target for every reported component. No
            update is initiated from the dashboard.
          </p>
          {softwareComponents.map((component) => {
            const policy = data.policies.find((candidate) =>
              candidate.component === component
            );
            return (
              <details key={component}>
                <summary>{component} policy</summary>
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    const values = new FormData(event.currentTarget);
                    void run(() =>
                      saveVersionPolicy({
                        component,
                        desiredVersion: String(values.get("version")),
                        minimumSupportedVersion: String(values.get("minimum")),
                        immutableRequired: values.get("immutable") === "on",
                      }, csrf), `${component} policy saved.`);
                  }}
                >
                  <label>
                    Desired version<input
                      name="version"
                      defaultValue={policy?.desiredVersion ?? ""}
                      required
                      maxLength={128}
                    />
                  </label>
                  <label>
                    Minimum supported version<input
                      name="minimum"
                      defaultValue={policy?.minimumSupportedVersion ?? ""}
                      maxLength={128}
                    />
                  </label>
                  <label>
                    <input
                      name="immutable"
                      type="checkbox"
                      defaultChecked={policy?.immutableRequired ?? true}
                    />{" "}
                    Require immutable evidence
                  </label>
                  <button type="submit">Save {component} policy</button>
                </form>
              </details>
            );
          })}
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Node</th>
                  {softwareComponents.map((component) => (
                    <th key={component}>{component}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.software.map((software) => (
                  <tr key={`${software.nodeId}-${software.target}`}>
                    <td>{software.projectNames.join(", ") || "Unassigned"}</td>
                    <td>{software.nodeName}</td>
                    {softwareComponents.map((component) => {
                      const policy = data.policies.find((candidate) =>
                        candidate.component === component
                      );
                      const reported = software[component];
                      return (
                        <td key={component}>
                          {policy
                            ? classifyCompliance(
                              reported ?? undefined,
                              policy.desiredVersion,
                              software.immutable && policy.immutableRequired,
                              policy.minimumSupportedVersion,
                            )
                            : "unknown"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="project-card" hidden={activeTab !== "incidents"} aria-labelledby="operations-tab-incidents">
          <h3>Incident evidence links</h3>
          <p>
            Open the retained node history supporting each correlated event.
          </p>
          {data.incidentEvents.some((event) => event.nodeId)
            ? (
              <ul>
                {data.incidentEvents.filter((event) => event.nodeId).map((
                  event,
                ) => (
                  <li key={`evidence-${event.id}`}>
                    <time dateTime={event.occurredAt}>
                      {new Date(event.occurredAt).toLocaleString()}
                    </time>{" "}
                    — {event.message} —{" "}
                    <a
                      href={`/nodes?node=${encodeURIComponent(event.nodeId ?? "")}`}
                    >
                      View node evidence
                    </a>
                  </li>
                ))}
              </ul>
            )
            : <p>No node-linked incident evidence is retained.</p>}
        </section>
        <section className="project-card" hidden={activeTab !== "configuration"} aria-labelledby="operations-tab-configuration">
          <NotificationPanel csrf={csrf} />
        </section>
        <section className="project-card" hidden={activeTab !== "configuration"} aria-labelledby="operations-tab-configuration">
          <h3>Map privacy</h3>
          <p>
            Project coordinates are released as exact, rounded to approximately
            one kilometre, or hidden. Map tiles are never loaded automatically.
          </p>
        </section>
      </div>
    </section>
  );
}
